import { usePinnedFeeds } from './pinnedFeeds';
import { isEscalationStale, useWallEscalation } from './selectionBridge';
import { hasWallOperatorState, resetWallOperatorState } from './wallReset';

/**
 * Wall RESET control (#371) — a compact overlay button on the wall display that
 * clears operator-placed feeds (pins #182/#216 + escalation #222) and returns
 * the wall to pure auto-curation.
 *
 * It renders ONLY while the wall is carrying operator state (`hasWallOperatorState`)
 * — when the wall is already auto-curating there is nothing to reset, so the
 * control hides and the wall's calm read-only display is untouched (the
 * read-only law forbids OPERATIONAL mutation; clearing an operator's own pin/
 * escalation is the inverse of the "Send to wall" act, not a new wall mutation,
 * and it rides the same cross-window persist+broadcast path).
 *
 * A real <button> (keyboard-accessible: focus + Enter/Space), pinned to the
 * wall's top-right corner — clear of the PEOC corner-tag (top-left), the GOC
 * hero identity (hero-zone top-right, not the wall corner), and the hero
 * escalation banner (top-centre). pointer-events-auto so it is clickable atop
 * the otherwise pointer-events-none overlay layer.
 */
export function WallResetControl(): JSX.Element | null {
  const { pins } = usePinnedFeeds();
  const escalation = useWallEscalation();

  // Match the media zone's read-time TTL filter (#222): a >30-min-old escalation
  // has already dropped off the wall (WallMediaZone's `toEscalatedTarget`), so it
  // must NOT keep the reset control alive — else the button ghosts on a wall that
  // is already auto-curating, breaking its own self-hide contract.
  const liveEscalation =
    escalation && !isEscalationStale(escalation, Date.now()) ? escalation : null;

  if (!hasWallOperatorState(pins, liveEscalation)) return null;

  return (
    <button
      type="button"
      data-testid="wall-reset"
      aria-label="Reset wall to auto-curation (clear pinned and escalated feeds)"
      title="Clear pinned + escalated feeds — return the wall to auto-curation"
      onClick={() => resetWallOperatorState()}
      className="pointer-events-auto absolute right-2 top-2 z-20 flex items-center gap-1.5 rounded-md border border-slate-600/60 bg-slate-950/80 px-2.5 py-1 text-xs font-medium text-slate-200 backdrop-blur transition-colors hover:bg-slate-900/90 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70"
    >
      <span aria-hidden="true">⟲</span>
      Reset wall
    </button>
  );
}
